import { jsPDF } from 'jspdf';

/**
 * Template Types:
 * - 'professional' (Free) - General purpose, clean design
 * - 'resurface'    (Free) - Bathtub / Countertop refinishing
 * - 'painting'     (Pro)  - Emphasizes materials, coats, prep work
 * - 'drywall'      (Pro)  - Focuses on patches, surface area
 * - 'cabinet'      (Pro)  - Highlights custom finishes, hardware
 * - 'electrician'  (Pro)  - Electrical work, hourly billing emphasis
 * - 'plumbing'     (Pro)  - Plumbing, parts + labor breakdown
 * - 'roofing'      (Pro)  - Roofing, sq-ft + materials emphasis
 * - 'windows'      (Pro)  - Window installation, per-unit pricing
 * - 'lawncare'     (Pro)  - Lawn / landscaping, recurring service ready
 * - 'cleaning'     (Pro)  - Residential / commercial cleaning
 */

const TEMPLATES = {
  professional: {
    name: 'Professional',
    tier: 'free',
    color: [37, 99, 235], // Blue
  },
  resurface: {
    name: 'Bathtub & Countertop Resurface',
    tier: 'free',
    color: [14, 165, 233], // Sky Blue
  },
  painting: {
    name: 'Painting Specialist',
    tier: 'pro',
    color: [34, 197, 94], // Green
  },
  drywall: {
    name: 'Drywall Pro',
    tier: 'pro',
    color: [168, 85, 247], // Purple
  },
  cabinet: {
    name: 'Cabinet Master',
    tier: 'pro',
    color: [234, 88, 12], // Orange
  },
  electrician: {
    name: 'Electrical Pro',
    tier: 'pro',
    color: [234, 179, 8], // Amber — high-visibility electrical
  },
  plumbing: {
    name: 'Plumbing Pro',
    tier: 'pro',
    color: [6, 182, 212], // Cyan — water trades
  },
  roofing: {
    name: 'Roofing Pro',
    tier: 'pro',
    color: [185, 28, 28], // Deep Red — sun-resilient
  },
  windows: {
    name: 'Window Installation',
    tier: 'pro',
    color: [71, 85, 105], // Slate — clean glass
  },
  lawncare: {
    name: 'Lawn Care & Landscaping',
    tier: 'pro',
    color: [22, 163, 74], // Emerald
  },
  cleaning: {
    name: 'Cleaning Service',
    tier: 'pro',
    color: [20, 184, 166], // Teal — fresh / clean
  },
};

// Per-template tailored copy. Used to swap the "Scope of Work" block,
// the cost-breakdown labels, the fallback warranty paragraph, and a small
// cover-letter line at the top of each PDF. Keeps each trade feeling
// hand-crafted instead of "same form, different color".
const TEMPLATE_COPY = {
  professional: {
    scopeHeading: 'PROJECT DETAILS',
    materialsLabel: 'Materials',
    laborLabel: 'Labor',
    paymentDefault: 'Net 30. 50% deposit due on acceptance, balance on completion.',
    warrantyDefault: 'Workmanship guaranteed for 1 year from completion date.',
    coverNote: 'Thank you for the opportunity to quote your project. Below is a detailed breakdown for your review.',
  },
  resurface: {
    scopeHeading: 'SURFACE PREP & RESURFACING SCOPE',
    materialsLabel: 'Coatings & Primer',
    laborLabel: 'Surface Prep + Application',
    paymentDefault: '50% deposit on acceptance, balance on completion. Curing time included.',
    warrantyDefault: 'Resurfacing finish guaranteed against peeling, bubbling, or yellowing for 5 years with normal use.',
    coverNote: 'This quote covers full surface preparation, three-layer coating, and 24-48 hour curing for a like-new finish.',
  },
  painting: {
    scopeHeading: 'PAINTING SCOPE',
    materialsLabel: 'Paint & Primer',
    laborLabel: 'Prep, Cut-in & Roll',
    paymentDefault: '50% deposit on acceptance, balance on completion.',
    warrantyDefault: 'Paint application warranted against peeling and blistering for 2 years.',
    coverNote: 'Includes surface prep, primer where needed, and two finish coats for a smooth, lasting result.',
  },
  drywall: {
    scopeHeading: 'DRYWALL REPAIR SCOPE',
    materialsLabel: 'Patches, Mud & Tape',
    laborLabel: 'Patch, Sand & Texture',
    paymentDefault: 'Net 15. 50% deposit on acceptance, balance on completion.',
    warrantyDefault: 'Drywall repairs guaranteed against cracking or settling for 1 year.',
    coverNote: 'Quote covers patching, sanding, texture-matching, and prime-ready finish on all listed surfaces.',
  },
  cabinet: {
    scopeHeading: 'CABINET REFINISHING SCOPE',
    materialsLabel: 'Stain, Sealer & Hardware',
    laborLabel: 'Strip, Sand & Refinish',
    paymentDefault: '50% deposit on acceptance, balance on completion.',
    warrantyDefault: 'Cabinet finish guaranteed against peeling, chipping, or yellowing for 3 years.',
    coverNote: 'Custom refinishing of cabinet faces and frames including stripping, multi-stage sanding, and protective topcoat.',
  },
  electrician: {
    scopeHeading: 'ELECTRICAL WORK — SCOPE & SAFETY',
    materialsLabel: 'Wire, Devices & Materials',
    laborLabel: 'Licensed Electrical Labor',
    paymentDefault: 'Net 15. 50% deposit on acceptance, balance on completion. Permit fees billed at cost.',
    warrantyDefault: 'All electrical work performed to current NEC code. Workmanship guaranteed for 2 years; parts per manufacturer warranty.',
    coverNote: 'All work performed by licensed electricians and inspected for code compliance. Permit pulled where required.',
  },
  plumbing: {
    scopeHeading: 'PLUMBING WORK — PARTS & LABOR',
    materialsLabel: 'Fixtures, Pipe & Fittings',
    laborLabel: 'Licensed Plumbing Labor',
    paymentDefault: 'Net 15. 50% deposit on acceptance, balance on completion. Emergency callouts billed separately.',
    warrantyDefault: 'Plumbing workmanship guaranteed for 2 years against leaks and joint failure. Fixtures covered by manufacturer warranty.',
    coverNote: 'All work performed by licensed plumbers, inspected before walls close, and tested under pressure before sign-off.',
  },
  roofing: {
    scopeHeading: 'ROOFING SCOPE — MATERIALS & UNDERLAYMENT',
    materialsLabel: 'Shingles, Underlayment & Flashing',
    laborLabel: 'Tear-off & Install',
    paymentDefault: '30% deposit on materials delivery, 40% on dry-in, 30% on final inspection.',
    warrantyDefault: 'Roof installation warranted for 10 years against leaks caused by workmanship. Shingles carry manufacturer warranty (typically 25-50 years).',
    coverNote: 'Includes tear-off of existing layer, drip edge, synthetic underlayment, ice & water shield in valleys, and full-spec shingle install.',
  },
  windows: {
    scopeHeading: 'WINDOW INSTALLATION SCOPE',
    materialsLabel: 'Windows, Flashing & Sealant',
    laborLabel: 'Removal & Install',
    paymentDefault: '50% deposit on order placement, balance on installation completion.',
    warrantyDefault: 'Installation warranted for 5 years against air/water leaks. Window units carry manufacturer warranty.',
    coverNote: 'Quote covers full removal of existing units, new flashing, foam insulation, and weather-tight finish on the listed openings.',
  },
  lawncare: {
    scopeHeading: 'LAWN CARE & LANDSCAPING SCOPE',
    materialsLabel: 'Mulch, Fertilizer & Materials',
    laborLabel: 'Crew Labor',
    paymentDefault: 'Due on completion of each visit. Recurring service available with 10% loyalty discount.',
    warrantyDefault: 'Service quality guaranteed — if you are unsatisfied with any visit, contact us within 48 hours and we will re-service free of charge.',
    coverNote: 'Per-visit pricing. Recurring weekly or bi-weekly service available at a 10% discount — ask about our seasonal contract.',
  },
  cleaning: {
    scopeHeading: 'CLEANING SERVICE SCOPE',
    materialsLabel: 'Supplies & Consumables',
    laborLabel: 'Cleaning Crew Labor',
    paymentDefault: 'Due on completion. Recurring service: invoiced monthly with 10% loyalty discount.',
    warrantyDefault: '100% satisfaction guarantee — if any area is unsatisfactory, notify us within 24 hours and we will return at no charge to make it right.',
    coverNote: 'Quote covers full scope of work listed. Recurring weekly/biweekly/monthly service available at preferred rates.',
  },
};

/**
 * Generate PDF with selected template
 * @param {Object} quoteData - Quote calculation data
 * @param {Object} leadData - Customer information
 * @param {Object} options - Additional options (template, branding, photos)
 * @returns {string} - Generated filename
 */
export const generateQuotePDFWithTemplate = (quoteData, leadData, options = {}) => {
  const {
    template = 'professional',
    branding = {},
    photos = [],
  } = options;

  const templateConfig = TEMPLATES[template] || TEMPLATES.professional;
  const doc = new jsPDF();

  // Colors
  const primaryColor = templateConfig.color;
  const textColor = [15, 23, 42]; // Slate-900
  const secondaryColor = [100, 116, 139]; // Slate-500
  const accentColor = branding.brand_color 
    ? hexToRgb(branding.brand_color) 
    : primaryColor;

  // Helper functions
  const addText = (text, x, y, options = {}) => {
    const { fontSize = 12, color = textColor, align = 'left', bold = false } = options;
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    if (bold) doc.setFont(undefined, 'bold');
    else doc.setFont(undefined, 'normal');
    doc.text(text, x, y, { align });
  };

  const addLine = (x1, y1, x2, y2, color = secondaryColor) => {
    doc.setDrawColor(...color);
    doc.line(x1, y1, x2, y2);
  };

  // Header Section
  let yPosition = 20;

  // Logo or Brand Box
  if (branding.logo_url) {
    // TODO: Add logo image when we implement image loading in jsPDF
    // For now, just show company name
    addText(branding.company_name || 'QuoteSnapp', 15, 23, {
      fontSize: 18,
      bold: true,
      color: accentColor,
    });
  } else {
    // Brand box
    doc.setFillColor(...accentColor);
    doc.rect(15, 15, 35, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(branding.company_name || 'QuoteSnapp', 32.5, 23, { align: 'center' });
  }

  // Company Info
  if (branding.company_address || branding.company_phone || branding.company_email) {
    let infoY = 24;
    if (branding.company_address) {
      addText(branding.company_address, 195, infoY, {
        fontSize: 9,
        align: 'right',
        color: secondaryColor,
      });
      infoY += 4;
    }
    if (branding.company_phone) {
      addText(branding.company_phone, 195, infoY, {
        fontSize: 9,
        align: 'right',
        color: secondaryColor,
      });
      infoY += 4;
    }
    if (branding.company_email) {
      addText(branding.company_email, 195, infoY, {
        fontSize: 9,
        align: 'right',
        color: secondaryColor,
      });
    }
  }

  yPosition = 42;

  // Title with template name badge
  addText('PROJECT QUOTE', 15, yPosition, {
    fontSize: 22,
    color: accentColor,
    bold: true,
  });
  addText(`- ${templateConfig.name}`, 75, yPosition, {
    fontSize: 10,
    color: secondaryColor,
  });

  yPosition += 6;
  addLine(15, yPosition, 195, yPosition, accentColor);
  yPosition += 10;

  // Customer Information
  if (leadData?.name || quoteData.customerName) {
    addText('Prepared For:', 15, yPosition, {
      fontSize: 10,
      color: secondaryColor,
    });
    yPosition += 6;
    addText(leadData?.name || quoteData.customerName || 'Valued Customer', 15, yPosition, {
      fontSize: 14,
      bold: true,
    });
    yPosition += 5;
  }

  if (leadData?.email) {
    addText(leadData.email, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }

  if (leadData?.phone) {
    addText(leadData.phone, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }

  if (leadData?.address) {
    addText(leadData.address, 15, yPosition, { fontSize: 10, color: secondaryColor });
    yPosition += 5;
  }

  yPosition += 3;

  // Date & Quote Number
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  addText(`Date: ${date}`, 15, yPosition, { fontSize: 10, color: secondaryColor });
  addText(`Quote #${Date.now().toString().slice(-6)}`, 195, yPosition, {
    fontSize: 10,
    color: secondaryColor,
    align: 'right',
  });
  yPosition += 12;

  const tCopy = TEMPLATE_COPY[template] || TEMPLATE_COPY.professional;

  // Cover note — small friendly intro that sells the trade
  if (tCopy.coverNote) {
    const lines = doc.splitTextToSize(tCopy.coverNote, 180);
    addText(lines, 15, yPosition, { fontSize: 9, color: secondaryColor });
    yPosition += lines.length * 4 + 6;
  }

  // Project Details Section (heading is now trade-tailored)
  addText(tCopy.scopeHeading, 15, yPosition, {
    fontSize: 14,
    color: accentColor,
    bold: true,
  });
  yPosition += 8;
  addLine(15, yPosition, 195, yPosition, accentColor);
  yPosition += 8;

  // Per-template scope rows. Each entry is [label, value | (qd)=>value].
  // We keep all 5 original templates plus the 6 new trades.
  const SCOPE_ROWS = {
    resurface: (qd) => {
      const surfaceCount = Math.ceil((qd.squareFeet || 0) / 25);
      return [
        ['Project Type:', qd.projectName],
        ['Surface Area:', `${qd.squareFeet} sq ft`],
        ['Surfaces to Resurface:', surfaceCount],
        ['Coating Layers:', '3 layers (primer + 2 topcoats)'],
        ['Curing Time:', '24-48 hours'],
        ['Labor Hours:', `${qd.laborHours} hours`],
      ];
    },
    painting: (qd) => [
      ['Project Type:', qd.projectName],
      ['Area:', `${qd.squareFeet} sq ft`],
      ['Coats:', '2 coats + primer where needed'],
      ['Surface Prep:', 'Cleaning, light sanding, masking'],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
    drywall: (qd) => [
      ['Project Type:', qd.projectName],
      ['Surface Area:', `${qd.squareFeet} sq ft`],
      ['Estimated Patches:', Math.ceil((qd.squareFeet || 0) / 50)],
      ['Finish:', 'Prime-ready, texture-matched'],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
    cabinet: (qd) => [
      ['Project Type:', qd.projectName],
      ['Cabinet Units:', Math.ceil((qd.squareFeet || 0) / 20)],
      ['Finish Type:', 'Custom Stain & Seal'],
      ['Hardware:', 'Reused or per spec — confirm before start'],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
    electrician: (qd) => [
      ['Project Type:', qd.projectName],
      ['Code Compliance:', 'Current NEC standards'],
      ['License:', 'Licensed & insured electrician on site'],
      ['Permit:', 'Pulled where required (billed at cost)'],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
    plumbing: (qd) => [
      ['Project Type:', qd.projectName],
      ['Trade License:', 'Licensed plumber on site'],
      ['Pressure Test:', 'Included before sign-off'],
      ['Permit:', 'Pulled where required (billed at cost)'],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
    roofing: (qd) => [
      ['Project Type:', qd.projectName],
      ['Roof Area:', `${qd.squareFeet} sq ft`],
      ['Squares:', `${Math.ceil((qd.squareFeet || 0) / 100)} (≈100 sq ft each)`],
      ['Tear-off:', 'Existing layer removed & disposed'],
      ['Underlayment:', 'Synthetic + ice & water in valleys'],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
    windows: (qd) => [
      ['Project Type:', qd.projectName],
      ['Windows:', qd.count || 'See scope'],
      ['Removal:', 'Existing units removed & disposed'],
      ['Sealing:', 'Foam insulation + flashing tape'],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
    lawncare: (qd) => [
      ['Service:', qd.projectName],
      ['Coverage:', `${qd.count || 1} yard / visit`],
      ['Equipment:', 'Crew-supplied'],
      ['Cleanup:', 'Full debris haul-off included'],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
    cleaning: (qd) => [
      ['Service:', qd.projectName],
      ['Crew Hours:', `${qd.hours || qd.laborHours} hours`],
      ['Supplies:', 'Eco-friendly, pet-safe products'],
      ['Frequency:', 'One-time (recurring discount available)'],
    ],
    professional: (qd) => [
      ['Project Type:', qd.projectName],
      ['Area:', `${qd.squareFeet} sq ft`],
      ['Labor Hours:', `${qd.laborHours} hours`],
    ],
  };

  const rows = (SCOPE_ROWS[template] || SCOPE_ROWS.professional)(quoteData);
  rows.forEach(([label, value]) => {
    addText(label, 15, yPosition, { fontSize: 11, color: secondaryColor });
    addText(String(value ?? '—'), 80, yPosition, { fontSize: 11, bold: true });
    yPosition += 6;
  });

  yPosition += 8;

  // Cost Breakdown Section
  addText('COST BREAKDOWN', 15, yPosition, {
    fontSize: 14,
    color: accentColor,
    bold: true,
  });
  yPosition += 8;
  addLine(15, yPosition, 195, yPosition, accentColor);
  yPosition += 10;

  // Line items — labels swap per template
  const lineItems = [
    [tCopy.materialsLabel, `$${quoteData.materialCost}`],
    [tCopy.laborLabel, `$${quoteData.laborCost}`],
  ];

  if (parseFloat(quoteData.addOnsCost) > 0) {
    lineItems.push(['Add-ons', `$${quoteData.addOnsCost}`]);
  }

  lineItems.forEach(([item, amount]) => {
    addText(item, 15, yPosition, { fontSize: 11 });
    addText(amount, 195, yPosition, { fontSize: 11, align: 'right' });
    yPosition += 6;
  });

  yPosition += 3;
  addLine(15, yPosition, 195, yPosition);
  yPosition += 7;

  // Subtotal
  addText('Subtotal', 15, yPosition, { fontSize: 11, color: secondaryColor });
  addText(`$${quoteData.subtotal}`, 195, yPosition, {
    fontSize: 11,
    color: secondaryColor,
    align: 'right',
  });
  yPosition += 6;

  // Markup
  addText(`Markup (${quoteData.markupPercent}%)`, 15, yPosition, {
    fontSize: 11,
    color: secondaryColor,
  });
  addText(`$${quoteData.markup}`, 195, yPosition, {
    fontSize: 11,
    color: secondaryColor,
    align: 'right',
  });
  yPosition += 8;

  // Total (highlighted)
  const lightAccent = accentColor.map((c) => Math.min(c + 200, 255));
  doc.setFillColor(...lightAccent);
  doc.rect(13, yPosition - 5, 184, 10, 'F');

  addText('TOTAL', 15, yPosition, { fontSize: 14, bold: true });
  addText(`$${quoteData.total}`, 195, yPosition, {
    fontSize: 16,
    color: [34, 197, 94],
    bold: true,
    align: 'right',
  });
  yPosition += 15;

  // Payment Terms Section — use the user's custom terms or a trade-specific default
  const paymentText = branding.payment_terms || tCopy.paymentDefault;
  if (paymentText) {
    addText('PAYMENT TERMS', 15, yPosition, {
      fontSize: 12,
      color: accentColor,
      bold: true,
    });
    yPosition += 7;
    addLine(15, yPosition, 195, yPosition);
    yPosition += 7;
    const ptLines = doc.splitTextToSize(paymentText, 180);
    addText(ptLines, 15, yPosition, {
      fontSize: 9,
      color: secondaryColor,
    });
    yPosition += ptLines.length * 4 + 6;
  }

  // Warranty Section — user's custom warranty or a trade-specific default
  const warrantyText = branding.warranty_text || tCopy.warrantyDefault;
  if (warrantyText) {
    addText('WARRANTY', 15, yPosition, { fontSize: 12, color: accentColor, bold: true });
    yPosition += 7;
    addLine(15, yPosition, 195, yPosition);
    yPosition += 7;
    const wtLines = doc.splitTextToSize(warrantyText, 180);
    addText(wtLines, 15, yPosition, {
      fontSize: 9,
      color: secondaryColor,
    });
    yPosition += wtLines.length * 4 + 6;
  }

  // Signature Section
  addText('CUSTOMER SIGNATURE', 15, yPosition, {
    fontSize: 12,
    color: accentColor,
    bold: true,
  });
  yPosition += 10;
  addLine(15, yPosition, 100, yPosition); // Signature line
  yPosition += 5;
  addText('Date:', 15, yPosition, { fontSize: 9, color: secondaryColor });
  addLine(30, yPosition - 1, 70, yPosition - 1); // Date line
  yPosition += 10;

  // ──────────────────────────────────────────────────────────────────
  // PROJECT PHOTOS (optional, only when the AI Photo Estimator was used)
  // Embeds up to 6 thumbnails in a 2-column grid on a new page so the
  // quote feels like a proper inspection report. Each image cell is
  // 85mm × 55mm with a 4mm gutter; jsPDF handles the PNG/JPEG encoding.
  // ──────────────────────────────────────────────────────────────────
  if (Array.isArray(photos) && photos.length > 0) {
    doc.addPage();
    let py = 20;
    addText('PROJECT PHOTOS', 15, py, { fontSize: 16, color: accentColor, bold: true });
    py += 6;
    addLine(15, py, 195, py, accentColor);
    py += 8;
    addText(
      'Reference photos provided with this quote for context. Conditions may differ once work begins.',
      15, py, { fontSize: 9, color: secondaryColor }
    );
    py += 8;

    const cellW = 85;
    const cellH = 55;
    const gutter = 5;
    const leftX = 15;
    const rightX = leftX + cellW + gutter;
    const maxPhotos = Math.min(photos.length, 6);

    for (let i = 0; i < maxPhotos; i++) {
      const p = photos[i];
      const dataUrl = typeof p === 'string' ? p : (p?.dataUrl || p?.url || p?.data_url);
      if (!dataUrl) continue;

      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = col === 0 ? leftX : rightX;
      const y = py + row * (cellH + gutter + 4);

      // Page-break safety — if a row would overflow, start a fresh page.
      if (y + cellH > 280) {
        doc.addPage();
        py = 20;
      }

      try {
        // Infer format from data URL prefix; fall back to JPEG.
        let fmt = 'JPEG';
        if (dataUrl.startsWith('data:image/png')) fmt = 'PNG';
        else if (dataUrl.startsWith('data:image/webp')) fmt = 'WEBP';
        doc.addImage(dataUrl, fmt, x, y, cellW, cellH, undefined, 'FAST');
        // Optional caption
        if (typeof p === 'object' && p?.caption) {
          addText(p.caption, x, y + cellH + 4, { fontSize: 8, color: secondaryColor });
        } else {
          addText(`Photo ${i + 1}`, x, y + cellH + 4, { fontSize: 8, color: secondaryColor });
        }
      } catch (err) {
        // Bad image (corrupt / unsupported) — skip silently rather than blow up the PDF.
        addText(`(Photo ${i + 1} unavailable)`, x, y + 20, { fontSize: 9, color: secondaryColor });
      }
    }
  }

  // Footer
  yPosition = 280;
  addLine(15, yPosition, 195, yPosition);
  yPosition += 5;
  addText(
    `Generated by ${branding.company_name || 'QuoteSnapp'} - Professional Quote Generation`,
    105,
    yPosition,
    {
      fontSize: 8,
      color: secondaryColor,
      align: 'center',
    }
  );

  // Generate filename
  const customerSlug = (leadData?.name || quoteData.customerName || 'customer')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
  const filename = `quote-${template}-${customerSlug}-${Date.now()}.pdf`;

  // Save the PDF
  doc.save(filename);

  return filename;
};

// Helper function to convert hex color to RGB
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [37, 99, 235]; // Default blue
}

// Export templates list for UI
export { TEMPLATES };
